import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CONFORMITY,
  CONFORMITY_LABELS,
  type Conformity,
  type InternalAudit,
  type InternalAuditFinding,
  type InternalAuditItem,
  type UpdateInternalAuditItemInput,
} from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import {
  Badge,
  Button,
  Dialog,
  Field,
  Select,
  Textarea,
  formatDateTime,
  useToast,
} from '../../components/ui.js';
import { FindingEditor } from './FindingEditor.js';
import {
  ConformityBadge,
  ItemStatusBadge,
  auditKey,
  errorMessage,
  isStale,
  itemsKey,
} from './shared.js';

/**
 * Manual result override and the auditor's note for one clause — used inside
 * the item dialog and on its own in the quick "Note" dialog.
 */
export function ItemAssessmentForm({
  auditId,
  item,
  onSaved,
}: {
  auditId: string;
  item: InternalAuditItem;
  onSaved?: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [conformity, setConformity] = useState<Conformity>(item.conformity);
  const [note, setNote] = useState(item.auditorNote);
  // Follow server-side changes (an AI run finishing on the clause being looked
  // at) field by field: a field the auditor has not touched since the last
  // stored value takes the new one, a field being edited keeps what was typed.
  // A different clause resets both.
  const synced = useRef({ id: item.id, conformity: item.conformity, note: item.auditorNote });
  useEffect(() => {
    const previous = synced.current;
    const newItem = previous.id !== item.id;
    if (newItem || conformity === previous.conformity) setConformity(item.conformity);
    if (newItem || note === previous.note) setNote(item.auditorNote);
    synced.current = { id: item.id, conformity: item.conformity, note: item.auditorNote };
  }, [item.id, item.conformity, item.auditorNote]);

  const dirty = conformity !== item.conformity || note !== item.auditorNote;
  const save = useMutation({
    mutationFn: () => {
      const body: UpdateInternalAuditItemInput = {};
      if (conformity !== item.conformity) body.conformity = conformity;
      if (note !== item.auditorNote) body.auditorNote = note;
      return apiFetch<InternalAuditItem>(`/internal-audits/${auditId}/items/${item.id}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: itemsKey(auditId) });
      void queryClient.invalidateQueries({ queryKey: auditKey(auditId) });
      toast('Clause updated.', 'success');
      onSaved?.();
    },
    onError: (err) => toast(errorMessage(err, 'Could not update the clause'), 'error'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (dirty && !save.isPending) save.mutate();
  };

  return (
    <form onSubmit={submit} className="audit-item-form" aria-label="Auditor assessment">
      <Field label="Result" hint="Overrides the AI auditor’s verdict for this clause">
        {(props) => (
          <Select {...props} value={conformity} onChange={(e) => setConformity(e.target.value as Conformity)}>
            {CONFORMITY.map((c) => (
              <option key={c} value={c}>
                {CONFORMITY_LABELS[c]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Auditor note" hint="Your own observations on this clause (kept when the clause is re-run)">
        {(props) => (
          <Textarea {...props} rows={3} maxLength={5000} value={note} onChange={(e) => setNote(e.target.value)} />
        )}
      </Field>
      <div className="dialog-actions" style={{ marginTop: 0 }}>
        <Button type="submit" size="sm" loading={save.isPending} disabled={!dirty}>
          Save assessment
        </Button>
      </div>
    </form>
  );
}

function ExaminedList({ title, entries }: { title: string; entries: string[] }) {
  return (
    <details className="audit-examined-group">
      <summary>
        {title} ({entries.length})
      </summary>
      {entries.length === 0 ? (
        <p className="audit-muted">None</p>
      ) : (
        <ul>
          {entries.map((entry, i) => (
            <li key={`${entry}-${i}`}>{entry}</li>
          ))}
        </ul>
      )}
    </details>
  );
}

/**
 * One clause in scope: the AI auditor's summary, what it examined, the
 * auditor's own assessment and the findings with inline editing.
 */
export function ItemDialog({
  audit,
  item,
  open,
  onClose,
  canEdit,
  canRerun,
  rerunning,
  onRerun,
  onRaise,
  onDelete,
}: {
  audit: InternalAudit;
  item: InternalAuditItem | null;
  open: boolean;
  onClose: () => void;
  /** Findings and the assessment can be edited (admin/reviewer, audit not approved). */
  canEdit: boolean;
  /** The AI auditor may be re-run (AI on, audit open, admin/reviewer). */
  canRerun: boolean;
  rerunning: boolean;
  onRerun: (item: InternalAuditItem) => void;
  onRaise: (finding: InternalAuditFinding) => void;
  onDelete: (finding: InternalAuditFinding) => void;
}) {
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (!open) setAdding(false);
  }, [open, item?.id]);

  const title = item ? `${item.clauseNumber} ${item.clauseTitle}` : 'Clause';
  const examined = item?.examined;
  const examinedTotal = examined
    ? examined.responses.length + examined.evidence.length + examined.documents.length + examined.risks.length
    : 0;
  const rerunnable =
    !!item && (['done', 'failed', 'skipped'].includes(item.status) || isStale(item));

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      {item && (
        <div className="audit-item-dialog">
          <div className="audit-badges">
            <ItemStatusBadge status={item.status} />
            {isStale(item) && <Badge tone="warning">Stale — the run that claimed it stopped</Badge>}
            {item.conformity !== 'not_audited' && <ConformityBadge conformity={item.conformity} />}
            {item.runAt && <span className="audit-muted">examined {formatDateTime(item.runAt)}</span>}
            {canRerun && rerunnable && (
              <Button size="sm" variant="secondary" loading={rerunning} onClick={() => onRerun(item)}>
                Re-run AI auditor
              </Button>
            )}
          </div>
          {item.error && (
            <p className="audit-error" role="alert">
              {item.error}
            </p>
          )}

          {item.aiSummary && (
            <section className="audit-ai-summary" aria-labelledby={`ai-summary-${item.id}`}>
              <h4 id={`ai-summary-${item.id}`}>
                AI auditor summary <Badge tone="purple">AI-generated</Badge>
                {item.aiModel && <Badge tone="neutral">{item.aiModel}</Badge>}
              </h4>
              <p className="audit-pre">{item.aiSummary}</p>
              <p className="audit-muted">
                Drafted by the AI desk-audit assistant from the material listed below. It is not
                authoritative — review and correct it before relying on it.
              </p>
            </section>
          )}

          <section className="audit-examined" aria-label="Material examined">
            <h4>Material examined</h4>
            {examinedTotal === 0 ? (
              <p className="audit-muted">
                {item.status === 'pending'
                  ? 'Nothing examined yet — run the AI auditor or record findings by hand.'
                  : 'No responses, evidence, documents or risks were available for this clause.'}
              </p>
            ) : (
              <>
                <details className="audit-examined-group" open>
                  <summary>Responses ({examined!.responses.length})</summary>
                  {examined!.responses.length === 0 ? (
                    <p className="audit-muted">None</p>
                  ) : (
                    <ul>
                      {examined!.responses.map((r) => (
                        <li key={r.responseId}>
                          {r.questionText} <Badge tone="neutral">{r.status.replace(/_/g, ' ')}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </details>
                <ExaminedList title="Evidence files" entries={examined!.evidence} />
                <ExaminedList title="Documents" entries={examined!.documents} />
                <ExaminedList title="Risks" entries={examined!.risks} />
              </>
            )}
          </section>

          {canEdit ? (
            <section aria-label="Auditor assessment">
              <h4>Auditor assessment</h4>
              <ItemAssessmentForm auditId={audit.id} item={item} />
            </section>
          ) : (
            item.auditorNote && (
              <section aria-label="Auditor note">
                <h4>Auditor note</h4>
                <p className="audit-pre">{item.auditorNote}</p>
              </section>
            )
          )}

          <section aria-label="Findings">
            <div className="audit-section-head">
              <h4>
                Findings ({item.findings.length})
              </h4>
              {canEdit && !adding && (
                <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                  Add finding
                </Button>
              )}
            </div>
            {item.findings.length === 0 && !adding && (
              <p className="audit-muted">No findings recorded for this clause.</p>
            )}
            {item.findings.map((f) => (
              <FindingEditor
                key={f.id}
                auditId={audit.id}
                itemId={item.id}
                finding={f}
                canEdit={canEdit}
                onRaise={onRaise}
                onDelete={onDelete}
              />
            ))}
            {adding && (
              <FindingEditor
                auditId={audit.id}
                itemId={item.id}
                finding={null}
                canEdit={canEdit}
                onRaise={onRaise}
                onDelete={onDelete}
                onCancelNew={() => setAdding(false)}
              />
            )}
          </section>

          <div className="dialog-actions">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
